import { ConciliationCaseApproval } from "@/@types/conciliation-history";
import { formatDateTime2 } from "@/utils/format-date";

import { approvalDeciderOf } from "../../utils/actor-label";
import { itemIdLabelOf } from "../../utils/change-label";
import { STATUS_VARIANTS } from "../../utils/constants";
import {
  CASE_TIMELINE_LABELS,
  NOT_AVAILABLE,
  TEXT_SEPARATORS,
} from "../../utils/messages";
import {
  CASE_APPROVAL_ROW_TEST_ID,
  CASE_APPROVALS_TEST_ID,
} from "../../utils/case-history-test-ids";
import { StatusBadge } from "../shared/status-badge";

interface CaseApprovalsSummaryProps {
  approvals: readonly ConciliationCaseApproval[];
}

interface ApprovalDecisionProps {
  approval: ConciliationCaseApproval;
}

function ApprovalDecision({ approval }: ApprovalDecisionProps): JSX.Element {
  const decider: string | null = approvalDeciderOf(approval);

  return (
    <span className="text-xs text-gray-700">
      {decider ? (
        <>
          {CASE_TIMELINE_LABELS.APPROVAL_DECIDED_BY}
          {TEXT_SEPARATORS.WORD}
          <strong>{decider}</strong>
        </>
      ) : (
        CASE_TIMELINE_LABELS.APPROVAL_DECIDED
      )}
      {TEXT_SEPARATORS.INLINE}
      {formatDateTime2(approval.decided_at, {
        fallback: NOT_AVAILABLE,
      })}
    </span>
  );
}

export function CaseApprovalsSummary({
  approvals,
}: CaseApprovalsSummaryProps): JSX.Element | null {
  if (approvals.length === 0) return null;

  return (
    <section className="mb-4" data-testid={CASE_APPROVALS_TEST_ID}>
      <h3 className="mb-2 text-sm font-semibold text-[#613F4F]">
        {CASE_TIMELINE_LABELS.APPROVALS_TITLE}
      </h3>

      <ul className="space-y-2">
        {approvals.map((approval: ConciliationCaseApproval) => (
          <li
            key={approval.id}
            className="rounded border border-gray-200 p-2"
            data-testid={CASE_APPROVAL_ROW_TEST_ID}
          >
            <div className="flex flex-wrap items-center justify-between gap-2">
              <ApprovalDecision approval={approval} />

              <StatusBadge
                variant={
                  approval.invalidated_at
                    ? STATUS_VARIANTS.CLOSED
                    : STATUS_VARIANTS.APPROVED
                }
              >
                {approval.invalidated_at
                  ? CASE_TIMELINE_LABELS.APPROVAL_INVALIDATED
                  : CASE_TIMELINE_LABELS.APPROVAL_VALID}
              </StatusBadge>
            </div>

            {approval.payroll_voucher_id !== null && (
              <span className="text-xs text-gray-600">
                {CASE_TIMELINE_LABELS.APPROVAL_VOUCHER}
                {TEXT_SEPARATORS.WORD}
                {itemIdLabelOf(approval.payroll_voucher_id)}
              </span>
            )}

            {approval.reconstructed && (
              <p className="mt-1 text-xs italic text-gray-500">
                {CASE_TIMELINE_LABELS.RECONSTRUCTED}
              </p>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}
